import { useState } from 'react';
import type { ProjectSearchResult } from '@/api/planning';
import type { AddMeerwerkLine } from '@/api/onderaannemersPoEdit';
import { AddLineModalShell } from './AddLineModalShell';
import { ProjectPicker } from './ProjectPicker';

interface AddMeerwerkModalProps {
  onAdd: (payload: AddMeerwerkLine) => Promise<void>;
  onClose: () => void;
}

function parseDecimal(raw: string): number | null {
  const n = Number(raw.replace(',', '.').trim());
  if (!Number.isFinite(n)) return null;
  const rounded = Math.round(n * 100) / 100;
  return rounded > 0 ? rounded : null;
}

const euro = new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR' });

/**
 * Add a general "Meerwerk onderaannemer" line: a free description billed at a typed
 * amount per unit (aantal defaults to 1). Manual price → audit-flagged on the backend.
 */
export function AddMeerwerkModal({ onAdd, onClose }: AddMeerwerkModalProps) {
  const [project, setProject] = useState<ProjectSearchResult | null>(null);
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [qty, setQty] = useState('1');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const parsedAmount = parseDecimal(amount);
  const parsedQty = parseDecimal(qty);
  const descOk = description.trim().length > 0;
  const canSubmit = !!project && descOk && parsedAmount != null && parsedQty != null;

  const total = parsedAmount != null && parsedQty != null ? parsedAmount * parsedQty : null;

  const handleSubmit = async () => {
    if (!canSubmit || !project || parsedAmount == null || parsedQty == null || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await onAdd({
        kind: 'meerwerk',
        projectId: project.id,
        description: description.trim(),
        amount: parsedAmount,
        qty: parsedQty,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Meerwerk toevoegen mislukt');
      setSubmitting(false);
    }
  };

  return (
    <AddLineModalShell
      title="Meerwerk toevoegen"
      submitLabel="Toevoegen"
      submitting={submitting}
      canSubmit={canSubmit}
      error={error}
      onSubmit={handleSubmit}
      onClose={onClose}
    >
      <div>
        <label className="block text-sm font-semibold text-td-navy mb-1">Project</label>
        <ProjectPicker selected={project} onSelect={setProject} autoFocus />
      </div>
      <div>
        <label htmlFor="add-mw-desc" className="block text-sm font-semibold text-td-navy mb-1">Omschrijving</label>
        <input
          id="add-mw-desc"
          type="text"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Bijv. Extra sparingen boren"
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-td-navy focus:outline-none focus:ring-2 focus:ring-td-blue"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor="add-mw-qty" className="block text-sm font-semibold text-td-navy mb-1">Aantal</label>
          <input
            id="add-mw-qty"
            type="text"
            inputMode="decimal"
            value={qty}
            onChange={(e) => setQty(e.target.value)}
            placeholder="1"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-right focus:outline-none focus:ring-2 focus:ring-td-blue"
          />
        </div>
        <div>
          <label htmlFor="add-mw-amount" className="block text-sm font-semibold text-td-navy mb-1">€ / stuk</label>
          <input
            id="add-mw-amount"
            type="text"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0,00"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-right focus:outline-none focus:ring-2 focus:ring-td-blue"
          />
        </div>
      </div>
      {total != null && (
        <p className="text-xs font-medium text-td-navy">Totaal: {euro.format(total)}</p>
      )}
    </AddLineModalShell>
  );
}
